/**
 * O cliente HTTP.
 *
 * Três responsabilidades que precisam morar juntas, porque separá-las
 * produz bug: injetar o token, renovar quando ele expira, e distinguir
 * 401 de 403.
 *
 * ## 401 e 403 não são a mesma coisa
 *
 *   401  a sessão acabou            → renova e repete; se falhar, login
 *   403  a sessão vale, o perfil não → mostra "sem permissão", NÃO desloga
 *
 * Tratar 403 como 401 é o erro clássico, e o sintoma é cruel: o
 * Professor clica em algo que não pode e é expulso do sistema, sem
 * entender por quê.
 *
 * ## A renovação é única
 *
 * Se cinco requisições esbarram no 401 ao mesmo tempo — e esbarram,
 * porque a tela dispara várias de uma vez —, cinco renovações
 * concorrentes rotacionam o refresh cinco vezes. A segunda a chegar
 * apresenta um token já usado, e o backend, corretamente, DERRUBA A
 * FAMÍLIA INTEIRA por detecção de reuso. O usuário é deslogado por um
 * mecanismo de segurança funcionando como deveria.
 *
 * Por isso `renovacaoEmCurso` é uma promessa compartilhada: quem chega
 * durante uma renovação espera a mesma, não começa outra.
 *
 * Ver: Arena_backend/Docs/plano/05-front-fundacao.md
 */

import type { SessaoAberta } from '@arena/shared/schemas';

const BASE = (import.meta.env['VITE_API_URL'] as string | undefined)?.replace(/\/$/, '') ?? '/api';

const CHAVE_ACESSO = 'arena.acesso';
const CHAVE_REFRESH = 'arena.refresh';

export class ErroDeApi extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly campos?: Record<string, string[]>,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ErroDeApi';
  }

  get ehPermissao(): boolean {
    return this.status === 403;
  }

  get ehValidacao(): boolean {
    return this.status === 400;
  }
}

/** Falha de rede — o servidor não respondeu. Diferente de erro dele. */
export class ErroDeRede extends Error {
  constructor() {
    super('Não consegui falar com o servidor.');
    this.name = 'ErroDeRede';
  }
}

/**
 * O texto que a tela mostra para um erro qualquer.
 *
 * A mensagem do servidor é usada como veio: ela já foi escrita para a
 * pessoa ("Quadra 1 já tem marcação às 19:00") e, no caso de
 * autorização, é genérica de propósito. Inventar outra aqui desfaria
 * as duas coisas.
 */
export function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ErroDeApi || erro instanceof ErroDeRede) return erro.message;
  return 'Não consegui completar esta operação. Tente de novo.';
}

/**
 * Resolve a URL de upload/leitura de um anexo ("Anexar comprovante",
 * documento 11) para o mesmo BASE que `api()` usa.
 *
 * Essas duas URLs (`EnvioDeAnexo.url`, `UrlDeAnexo.url`) NÃO passam por
 * `api()` — o upload é um `fetch` cru, sem `Authorization` nem
 * `content-type: application/json`. Isso expõe uma diferença entre os
 * dois drivers de Storage: o do Supabase devolve URL absoluta (passa
 * reto); o LOCAL — dev e Testcontainers, ver `Arena_backend/src/
 * armazenamento/local.ts` — devolve uma URL RELATIVA do próprio
 * backend (`/armazenamento/local/objeto?token=...`), sem o prefixo
 * `/api` que toda outra chamada ganha de `BASE`. Sem isso, o
 * navegador resolveria a URL contra a ORIGEM DO FRONT, e não existe
 * `/armazenamento/...` lá — só o proxy do Vite para o backend, sob
 * `/api`.
 */
export function comBase(caminho: string): string {
  return /^https?:\/\//.test(caminho) ? caminho : `${BASE}${caminho}`;
}

// ---------------------------------------------------------------------
// Tokens
//
// `localStorage` e não cookie httpOnly, e isso é uma escolha com
// trade-off declarado: o backend é uma API sem estado servida em outro
// domínio (api.… contra app.…), e cookie entre domínios exigiria
// SameSite=None mais CORS com credenciais — mais superfície, não menos.
// Em compensação, um XSS aqui alcança o token. A mitigação é a que já
// existe: acesso de 15 minutos e refresh com rotação e detecção de
// reuso, então um token roubado tem vida curta e o roubo é detectável.
// ---------------------------------------------------------------------

export const tokens = {
  ler(): { acesso: string | null; refresh: string | null } {
    return {
      acesso: localStorage.getItem(CHAVE_ACESSO),
      refresh: localStorage.getItem(CHAVE_REFRESH),
    };
  },
  gravar(sessao: SessaoAberta): void {
    localStorage.setItem(CHAVE_ACESSO, sessao.acesso);
    localStorage.setItem(CHAVE_REFRESH, sessao.refresh);
  },
  limpar(): void {
    localStorage.removeItem(CHAVE_ACESSO);
    localStorage.removeItem(CHAVE_REFRESH);
  },
};

/** Chamado quando a sessão morre de vez. O shell manda de volta ao login. */
let aoPerderSessao: () => void = () => {};
export function quandoPerderSessao(callback: () => void): void {
  aoPerderSessao = callback;
}

let renovacaoEmCurso: Promise<boolean> | null = null;

async function renovar(): Promise<boolean> {
  const { refresh } = tokens.ler();
  if (!refresh) return false;

  renovacaoEmCurso ??= (async () => {
    try {
      const resposta = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refresh }),
      });
      if (!resposta.ok) return false;
      tokens.gravar((await resposta.json()) as SessaoAberta);
      return true;
    } catch {
      return false;
    } finally {
      // Zerado no próximo tick para que as chamadas que já estavam
      // aguardando compartilhem ESTE resultado, e só as seguintes
      // comecem uma renovação nova.
      queueMicrotask(() => {
        renovacaoEmCurso = null;
      });
    }
  })();

  return renovacaoEmCurso;
}

interface Opcoes {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  corpo?: unknown;
  /** Rotas de login e refresh não levam token nem tentam renovar. */
  publica?: boolean;
}

export async function api<T>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  const resposta = await enviar(caminho, opcoes);

  if (resposta.status === 401 && !opcoes.publica) {
    if (await renovar()) {
      const segunda = await enviar(caminho, opcoes);
      if (segunda.status === 401) {
        tokens.limpar();
        aoPerderSessao();
      }
      return interpretar<T>(segunda);
    }
    tokens.limpar();
    aoPerderSessao();
  }

  return interpretar<T>(resposta);
}

async function enviar(caminho: string, opcoes: Opcoes): Promise<Response> {
  const { acesso } = tokens.ler();
  const cabecalhos: Record<string, string> = { 'content-type': 'application/json' };
  if (acesso && !opcoes.publica) cabecalhos['authorization'] = `Bearer ${acesso}`;

  try {
    return await fetch(`${BASE}${caminho}`, {
      method: opcoes.metodo ?? 'GET',
      headers: cabecalhos,
      ...(opcoes.corpo === undefined ? {} : { body: JSON.stringify(opcoes.corpo) }),
    });
  } catch {
    throw new ErroDeRede();
  }
}

// --- 09 · importação ---------------------------------------------------

/**
 * Um arquivo como CORPO CRU da requisição — o CSV de `#m-importar`.
 *
 * Mora aqui, e não num `fetch` solto no módulo da importação, pelas
 * mesmas três razões do topo: o token, a renovação única e a diferença
 * entre 401 e 403. A planilha é escolhida com calma, e o acesso de quinze
 * minutos vence com o modal aberto; um `fetch` por fora devolveria "faça
 * login" em vez de renovar — ou pior, renovaria em paralelo com a tela.
 *
 * Nem JSON nem multipart: o servidor precisa dos BYTES como vieram, para
 * detectar a codificação do Excel. O `File` vai direto no corpo.
 */
export async function enviarArquivo<T>(caminho: string, arquivo: Blob, tipo = 'text/csv'): Promise<T> {
  const enviarUmaVez = async (): Promise<Response> => {
    const { acesso } = tokens.ler();
    try {
      return await fetch(`${BASE}${caminho}`, {
        method: 'POST',
        headers: {
          'content-type': tipo,
          ...(acesso ? { authorization: `Bearer ${acesso}` } : {}),
        },
        body: arquivo,
      });
    } catch {
      throw new ErroDeRede();
    }
  };

  const resposta = await enviarUmaVez();
  if (resposta.status === 401) {
    if (await renovar()) {
      const segunda = await enviarUmaVez();
      if (segunda.status === 401) {
        tokens.limpar();
        aoPerderSessao();
      }
      return interpretar<T>(segunda);
    }
    tokens.limpar();
    aoPerderSessao();
  }
  return interpretar<T>(resposta);
}
// --- fim 09 --------------------------------------------------------------

async function interpretar<T>(resposta: Response): Promise<T> {
  if (resposta.status === 204) return undefined as T;

  const corpo = (await resposta.json().catch(() => ({}))) as {
    erro?: string;
    campos?: Record<string, string[]>;
    requestId?: string;
  };

  if (!resposta.ok) {
    throw new ErroDeApi(
      resposta.status,
      corpo.erro ?? 'Não consegui completar esta operação.',
      corpo.campos,
      corpo.requestId,
    );
  }

  return corpo as T;
}
